import { forwardRef, useId } from 'react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/shared/lib/cn';
import { formatMoney } from '@/shared/lib/money';

import styles from './AmountInput.module.css';

export interface AmountInputProps {
  label?: string;
  value: string;
  onChange: (value: string) => void;
  /** Максимум, каноническая строка. Показывается подсказкой и включает «всё». */
  max?: string;
  currency: string;
  error?: string;
  hint?: string;
  onMax?: () => void;
  disabled?: boolean;
  autoFocus?: boolean;
  placeholder?: string;
  className?: string;
}

/**
 * Маска ввода суммы.
 *
 * Держим строку, а не число: number-инпут в мобильных WebView сам «нормализует»
 * ввод (съедает запятую, меняет позицию каретки), а сумма — это строка от
 * начала до конца, включая передачу на сервер.
 */
function maskAmount(raw: string): string {
  const normalized = raw.replace(/,/g, '.').replace(/[^\d.]/g, '');
  const firstDot = normalized.indexOf('.');
  if (firstDot === -1) return normalized.replace(/^0+(?=\d)/, '');

  const head = normalized.slice(0, firstDot).replace(/^0+(?=\d)/, '');
  const tail = normalized.slice(firstDot + 1).replace(/\./g, '');
  return `${head === '' ? '0' : head}.${tail.slice(0, 2)}`;
}

export const AmountInput = forwardRef<HTMLInputElement, AmountInputProps>(function AmountInput(
  {
    label,
    value,
    onChange,
    max,
    currency,
    error,
    hint,
    onMax,
    disabled = false,
    autoFocus = false,
    placeholder = '0.00',
    className,
  },
  ref,
) {
  const { t } = useTranslation();
  const inputId = useId();
  const errorId = `${inputId}-error`;
  const hintId = `${inputId}-hint`;

  const showMax = typeof onMax === 'function' && max !== undefined;
  const effectiveHint =
    hint ?? (max !== undefined ? `${t('withdraw.available')}: ${formatMoney(max, currency)}` : undefined);

  return (
    <div className={cn(styles.field, disabled && styles.disabled, className)}>
      {label !== undefined ? (
        <label className={styles.label} htmlFor={inputId}>
          {label}
        </label>
      ) : null}

      <div className={cn(styles.shell, error !== undefined && styles.invalid)}>
        <input
          ref={ref}
          id={inputId}
          className={styles.input}
          value={value}
          onChange={(event) => onChange(maskAmount(event.target.value))}
          type="text"
          /* decimal вызывает числовую клавиатуру с точкой и без букв. */
          inputMode="decimal"
          autoComplete="off"
          autoCorrect="off"
          spellCheck={false}
          enterKeyHint="done"
          placeholder={placeholder}
          disabled={disabled}
          autoFocus={autoFocus}
          maxLength={16}
          aria-invalid={error !== undefined || undefined}
          aria-describedby={error ? errorId : effectiveHint ? hintId : undefined}
        />
        <span className={styles.currency} aria-hidden="true">
          {currency}
        </span>
        {showMax ? (
          <button type="button" className={styles.maxButton} onClick={onMax} disabled={disabled}>
            {t('withdraw.amount_max')}
          </button>
        ) : null}
      </div>

      {error !== undefined ? (
        <p className={styles.error} id={errorId} role="alert">
          {error}
        </p>
      ) : effectiveHint !== undefined ? (
        <p className={styles.hint} id={hintId}>
          {effectiveHint}
        </p>
      ) : null}
    </div>
  );
});
